/// FOURJS_START_COPYRIGHT(D,2015)
/// Property of Four Js*
/// (c) Copyright Four Js 2015, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

/**
 * See modulum import at the end of the file
 */
const AuiProtocolParser = (function () {
  let instructions = [];

  /**
   * Convert a VM order into instructions and AUI
   * EntryPoint
   * @param {string} vmOrderString
   * @return {*}
   */
  function parseOrder(vmOrderString) {
    //we may get multiple commands at once
    const commands = vmOrderString.split("\n").filter((o) => o.length > 0);
    instructions = [];
    for (const txt of commands) {
      parseTcl(txt);
    }
    return instructions;
  }

  const TYPE = {
    None: "None",
    Number: "Number",
    Value: "Value",
    Ident: "Ident"
  };

  /**
   *
   * @param parsingObject
   * @return {number|string}
   */
  function eatWS(parsingObject) {
    const { buf, pos } = parsingObject;

    if (parsingObject.pos >= buf.length) {
      return -1;
    }
    let u = buf.charAt(pos);

    while ((u === " " || u === "\n" || u === "\\") && parsingObject.pos < buf.length) {
      u = buf.charAt(++parsingObject.pos);
    }
    return u;
  }

  /**
   * Get char - TBD
   * @param {Object} parseObject
   * @return {number|string}
   */
  function getChar(parseObject) {
    const u = eatWS(parseObject);
    parseObject.pos++;
    return u;
  }

  /**
   * Get identifier on buffer
   * @param {Object} parsingObject
   */
  function identFromBuf(parsingObject) {
    const buf = parsingObject.buf;
    const len = buf.length;
    let u = buf.charAt(parsingObject.pos);
    let i = 0;
    // Performance critical here, don't use any functions
    while (u !== " " && u !== "{" && u !== "}" && parsingObject.pos < len) {
      u = buf.charAt(++parsingObject.pos);
      i++;
    }
    //copy ident portion to ident
    parsingObject.ident = buf.substring(parsingObject.pos - i, parsingObject.pos);
  }

  /**
   * getValueWithSeparator
   * @param parsingObject
   * @param separator
   * @return {string}
   */
  function getValueWithSeparator(parsingObject, separator) {
    let value = "";
    parsingObject.pos++; // remove first "
    const buf = parsingObject.buf;
    for (; parsingObject.pos < buf.length; parsingObject.pos++) {
      let u = buf.charAt(parsingObject.pos);

      if (u === "\\") {
        u = buf.charAt(++parsingObject.pos);
        value += u === "n" ? "\n" : u;
      } else if (u === separator) {
        parsingObject.pos++;
        break;
      } else {
        value += u;
      }
    }
    return value;
  }

  /**
   * Get the type of token
   * @param parsingObject
   * @return {string}
   */
  function getTokenType(parsingObject) {
    let numberBuffer = "";

    let u = eatWS(parsingObject);

    if (u === -1) {
      return TYPE.None;
    }

    while (u >= "0" && u <= "9") {
      // is digit (performance-critical: no regex)
      numberBuffer += u;
      u = parsingObject.buf.charAt(++parsingObject.pos);
    }

    if (numberBuffer !== "") {
      parsingObject.number = Number.parseInt(numberBuffer, 10);
      return TYPE.Number;
    }

    if (u === `"` || u === `'`) {
      parsingObject.value = getValueWithSeparator(parsingObject, u);
      return TYPE.Value;
    }

    identFromBuf(parsingObject);
    parsingObject.value = null;
    return TYPE.Ident;
  }

  /**
   * parseTcl
   * @param tclString
   */
  function parseTcl(tclString) {
    let parsingObject = {
      pos: 0,
      buf: tclString,
      number: Number.NaN,
      value: null,
      ident: null
    };
    let result = true;
    for (result; result && parsingObject.pos < tclString.length; parsingObject.pos++) {
      if (getTokenType(parsingObject) !== TYPE.Ident) {
        break;
      }
      if (parsingObject.ident === "meta") {
        result = handleParseMeta(parsingObject);
        break; // no need to continue to parse the string
      } else if (parsingObject.ident === "om") {
        result = handleParseOm(parsingObject);
      } else {
        console.error("PARSER: invalid token:" + parsingObject.ident);
        result = false;
      }
    }

    if (result === false) {
      console.error("PARSER: parseTcl failed");
    }
  }

  /**
   * handle META order
   * @param {Object} parsingObject
   * @return {boolean}
   */
  function handleParseMeta(parsingObject) {
    const [type, verb, ...attributes] = parsingObject.buf.split(" ");

    const attrString = attributes.join(" ").slice(1, -1);
    const parsedAttributes = {};
    const regex = /\{([^\s{}"]+)\s+"([^"]+)"}/g;
    let match = [];

    while ((match = regex.exec(attrString)) !== null) {
      const key = match[1];
      const rawValue = match[2];
      // Try to convert to number if it's numeric and not an ID-like string
      parsedAttributes[key] = parseValue(rawValue, key);
    }

    instructions.push({
      type,
      verb,
      attributes: parsedAttributes
    });

    return true;
  }

  /**
   * handle OM order
   * @param {Object} parsingObject
   * @return {boolean}
   */
  function handleParseOm(parsingObject) {
    const ordersToManage = [];
    const omNumber = Number.parseInt(parsingObject.buf.split(" ")[1], 10);

    if (getTokenType(parsingObject) !== TYPE.Number) {
      return false;
    }
    if (getChar(parsingObject) !== "{") {
      return false;
    }
    while (getChar(parsingObject) === "{") {
      if (getTokenType(parsingObject) !== TYPE.Ident) {
        return false;
      }
      const firstLetter = parsingObject.ident.charAt(0);
      if (firstLetter === "a") {
        //'a'n or 'a'ppendNode
        if (getTokenType(parsingObject) !== TYPE.Number) {
          return false;
        }
        const parentId = parsingObject.number;

        const node = handleAppendNode(parsingObject);

        let orderManage = {
          type: "add",
          parent: parentId,
          node: node
        };
        ordersToManage.push(orderManage);
      } else if (firstLetter === "u") {
        //'u'n or 'u'pdateNode
        if (getTokenType(parsingObject) !== TYPE.Number) {
          return false;
        }

        const updateInstruction = handleUpdateNode(parsingObject);
        if (!updateInstruction) {
          return false;
        }
        ordersToManage.push(updateInstruction);
      } else if (firstLetter === "r") {
        //'r'n or 'r'emoveNode
        if (getTokenType(parsingObject) !== TYPE.Number) {
          return false;
        }
        ordersToManage.push(handleRemoveNode(parsingObject.number));
      }
      if (getChar(parsingObject) !== "}") {
        return false;
      }
    }
    instructions.push({
      type: "om",
      id: omNumber,
      operations: ordersToManage
    });
    return true;
  }

  /**
   * Node attributes handle
   * @param {Object} parsingObject
   * @param {Object} node
   * @return {boolean}
   */
  function handleNodeAttr(parsingObject, node) {
    while (getChar(parsingObject) === "{") {
      if (getTokenType(parsingObject) !== TYPE.Ident) {
        return false;
      }
      const name = parsingObject.ident;
      if (getTokenType(parsingObject) !== TYPE.Value) {
        return false;
      }
      //set the attribute
      node.attributes[name] = parseValue(parsingObject.value, name);
      if (getChar(parsingObject) !== "}") {
        return false;
      }
    }
    return true;
  }

  /**
   * Give the correct type to the token string
   * @param {String} valueString
   * @param {String|null} valueType
   * @return {String|number|boolean}
   */
  function parseValue(valueString, valueType) {
    if (typeof valueString !== "string") {
      return valueString;
    }

    // Literal (string-typed) attributes must keep their raw textual value: they are never
    // coerced to a boolean nor to a number. Otherwise a legitimate string such as "true",
    // "false" or "42" stored in a value/text/name attribute would be turned into a boolean
    // or number and crash widgets expecting a string
    const literals = [
      "value",
      "name",
      "text",
      "comment",
      "valueUnchecked",
      "valueChecked",
      "style",
      "aggregateValue",
      "aggregateText",
      "acceleratorName",
      "acceleratorName2",
      "acceleratorName3",
      "acceleratorName4",
      "acceleratorKey1",
      "acceleratorKey2",
      "acceleratorKey3"
    ];
    const isLiteral = literals.includes(valueType);

    if (!isLiteral) {
      if (valueString === "true") return true;
      if (valueString === "false") return false;
    }

    // Remove quotes if it's a quoted string
    if (valueString.startsWith('"') && valueString.endsWith('"')) {
      valueString = valueString.slice(1, -1);
    }

    if (!isLiteral) {
      // Match integer or float, signed or unsigned
      if (/^[+-]?\d+(\.\d+)?$/.test(valueString)) {
        return Number(valueString);
      }
    }

    return valueString;
  }

  /**
   * Add a Node
   * @param {Object} parsingObject
   * @return {Object}
   */
  function handleAppendNode(parsingObject) {
    if (getTokenType(parsingObject) !== TYPE.Ident) {
      return null;
    }

    if (getTokenType(parsingObject) !== TYPE.Number) {
      return null;
    }

    if (getChar(parsingObject) !== "{") {
      return null;
    }

    const node = {
      id: parsingObject.number,
      type: parsingObject.ident,
      attributes: {}, //attributes store
      children: []
    };

    if (handleNodeAttr(parsingObject, node) === false) {
      return null;
    }

    if (getChar(parsingObject) !== "{") {
      return null;
    }

    while (getChar(parsingObject) === "{") {
      const ch = handleAppendNode(parsingObject);
      if (ch === null) {
        return null;
      }
      node.children.push(ch);
      if (getChar(parsingObject) !== "}") {
        return null;
      }
    }
    return node;
  }

  /**
   * Update a Node
   * @param {Object} parsingObject
   * @return {{attributes: {}, id: *, type: string}|boolean}
   */
  function handleUpdateNode(parsingObject) {
    const nodeId = parsingObject.number;

    if (getChar(parsingObject) !== "{") {
      return false;
    }
    let attributes = {};
    while (getChar(parsingObject) === "{") {
      if (getTokenType(parsingObject) !== TYPE.Ident) {
        return false;
      }
      const name = parsingObject.ident;
      if (getTokenType(parsingObject) !== TYPE.Value) {
        return false;
      }
      attributes[name] = parseValue(parsingObject.value, name);
      if (getChar(parsingObject) !== "}") {
        return false;
      }
    }

    return {
      type: "update",
      id: nodeId,
      attributes: attributes
    };
  }

  /**
   * Remove a node
   * @param {Number} id - of the node
   * @return {{id, type: string}}
   */
  function handleRemoveNode(id) {
    return {
      type: "remove",
      id: id
    };
  }

  return {
    parse: parseOrder
  };
})();

try {
  // Using Modulum
  modulum("AuiProtocolParser", (context) => {
    context.AuiProtocolParser = AuiProtocolParser;
  });
} catch (err) {
  (() => {})();
}
